import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import { hasAdminPrivileges } from '../../../utils/permissionsClient';
import '../../DashboardHome/settings.css';
import '../Settings/settings.css';

const ICN = {
    general: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 1.5v2M8 12.5v2M14.5 8h-2M3.5 8h-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    people: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="6" cy="6" r="2.4" stroke="currentColor" strokeWidth="1.5" />
            <path d="M2 13c.6-2 2.2-3 4-3s3.4 1 4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="11.5" cy="5.5" r="1.8" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    ),
    shield: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 2l5 1.8v4c0 3.2-2.6 5.2-5 6-2.4-.8-5-2.8-5-6v-4L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    ),
    code: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M5 4L2 8l3 4M11 4l3 4-3 4M9 3l-2 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    trash: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 5h10M6 5V3.5A1 1 0 017 2.5h2a1 1 0 011 1V5M5 5l1 8.5h4L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    arrow: (
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <path d="M2 5.5h7M6 2.5l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    plus: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    download: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M7 2v7m0 0l-3-3m3 3l3-3M2 11v1h10v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    copy: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <rect x="3" y="3" width="7" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" />
            <path d="M5 3V2a1 1 0 011-1h5a1 1 0 011 1v8a1 1 0 01-1 1h-1" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    ),
    device: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="2" y="3" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" />
            <path d="M6 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    key: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="5" cy="11" r="2.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M7 9l5.5-5.5M11 5l1.5 1.5M9 7l1.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    hook: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 3a3 3 0 013 3v4a3 3 0 01-6 0V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="8" cy="3" r="1.2" fill="currentColor" />
        </svg>
    ),
};

const SECTIONS = [
    { id: 'workspace', label: 'Workspace', icon: ICN.general, group: 'Administration' },
    { id: 'members', label: 'Members & roles', icon: ICN.people, group: 'Administration' },
    { id: 'security', label: 'Security', icon: ICN.shield, group: 'Administration' },
    { id: 'api', label: 'API & webhooks', icon: ICN.code, group: 'Developer' },
    { id: 'danger', label: 'Danger zone', icon: ICN.trash, group: 'Developer' },
];

const deepSet = (obj, path, value) => {
    const parts = path.split('.');
    const next = { ...obj };
    let cur = next;
    for (let i = 0; i < parts.length - 1; i += 1) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
    return next;
};

// ============ Primitives ============
const SetBlock = ({ title, desc, tag, danger, flush, children }) => (
    <div className={`set-block ${danger ? 'danger' : ''}`}>
        <div className={`set-block-head ${flush ? 'flush' : ''}`}>
            <h3>{title}{tag && <span className="tag">{tag}</span>}</h3>
            {desc && <p>{desc}</p>}
        </div>
        {children}
    </div>
);

const SetField = ({ label, desc, top, stack, children }) => (
    <div className={`set-field ${top ? 'top' : ''} ${stack ? 'stack' : ''}`}>
        <div className="set-field-label">
            <div className="lbl">{label}</div>
            {desc && <div className="desc">{desc}</div>}
        </div>
        <div className="set-field-control">{children}</div>
    </div>
);

const SetText = ({ value, onChange, placeholder, mono }) => (
    <input
        className={`set-input ${mono ? 'mono' : ''}`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
    />
);

const SetSelect = ({ value, onChange, options, minWidth }) => (
    <select
        className="set-input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={minWidth ? { minWidth } : undefined}
    >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
);

const SetToggle = ({ checked, onChange }) => (
    <button
        type="button"
        onClick={() => onChange(!checked)}
        style={{
            width: 38, height: 22, borderRadius: 999, border: 0,
            background: checked ? 'var(--accent)' : 'var(--bg-soft)',
            position: 'relative', cursor: 'pointer',
        }}
    >
        <span style={{
            position: 'absolute', top: 2,
            left: checked ? 18 : 2,
            width: 18, height: 18,
            background: 'white', borderRadius: 999,
            transition: 'left 0.15s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }} />
    </button>
);

// ============ Sections ============
const WorkspaceSection = ({ s, set }) => (
    <>
        <div className="set-section-head">
            <h2>Workspace</h2>
            <p>Identity and naming for your workspace. Visible to every member and on the public review portal.</p>
        </div>

        <SetBlock title="Identity">
            <div className="set-logo-row">
                <div className="set-logo-art">{((s.general.name || 'A').trim().charAt(0)).toUpperCase()}</div>
                <div className="set-logo-meta">
                    <b>Workspace logo</b>
                    <span>PNG or SVG, at least 128×128px</span>
                </div>
                <div className="set-logo-actions">
                    <button type="button" className="set-btn sm">Upload</button>
                    <button type="button" className="set-btn sm">Remove</button>
                </div>
            </div>
            <SetField label="Workspace name" desc="Shown in the sidebar, exports, and email notifications.">
                <SetText value={s.general.name} onChange={(v) => set('general.name', v)} />
            </SetField>
            <SetField label="Workspace URL" desc="Used for share links and the public review portal.">
                <SetText value={s.general.slug} onChange={(v) => set('general.slug', v)} placeholder="rush.studio/your-workspace" />
            </SetField>
            <SetField label="Description" desc="A short summary of what this workspace localizes." top>
                <textarea
                    className="set-input wide"
                    style={{ width: 320, minHeight: 70 }}
                    value={s.general.desc || ''}
                    onChange={(e) => set('general.desc', e.target.value)}
                />
            </SetField>
        </SetBlock>
    </>
);

const MembersSection = ({ s, set, roleCounts, navigate }) => {
    const m = s.members;
    const roleDefs = [
        { role: 'Owners', count: roleCounts.owners, desc: 'Full access including billing and deletion.' },
        { role: 'Admins', count: roleCounts.admins, desc: 'Manage members, security, and integrations.' },
        { role: 'Translators', count: roleCounts.translators, desc: 'Translate and confirm assigned segments.' },
        { role: 'Reviewers', count: roleCounts.reviewers, desc: 'Approve and comment on completed work.' },
    ];
    return (
        <>
            <div className="set-section-head">
                <h2>Members &amp; roles</h2>
                <p>Control who can join your workspace and what new members can do.</p>
            </div>

            <SetBlock title="Roles" flush>
                {roleDefs.map((r) => (
                    <div className="set-list-row" key={r.role}>
                        <div className="set-avatar">{ICN.people}</div>
                        <div className="li-main">
                            <b>{r.role}</b>
                            <span>{r.desc}</span>
                        </div>
                        <div className="grow" />
                        <span className="set-count">{r.count}</span>
                    </div>
                ))}
                <div className="set-list-row">
                    <div className="grow" />
                    <button type="button" className="set-btn sm" onClick={() => navigate('/dashboard/people')}>
                        Open People {ICN.arrow}
                    </button>
                </div>
            </SetBlock>

            <SetBlock title="Joining the workspace" desc="How new people are admitted and what they can do on day one.">
                <SetField label="Default role for new members" desc="Assigned automatically when someone joins.">
                    <SetSelect
                        value={m.defaultRole}
                        onChange={(v) => set('members.defaultRole', v)}
                        minWidth={180}
                        options={[
                            { value: 'translator', label: 'Translator' },
                            { value: 'reviewer', label: 'Reviewer' },
                            { value: 'admin', label: 'Admin' },
                            { value: 'viewer', label: 'Viewer (read-only)' },
                        ]}
                    />
                </SetField>
                <SetField label="Auto-join with company domain" desc="Anyone with a verified company email can request access.">
                    <SetToggle checked={!!m.autoJoinDomain} onChange={(v) => set('members.autoJoinDomain', v)} />
                </SetField>
                <SetField label="Require admin approval" desc="New requests must be approved before access is granted.">
                    <SetToggle checked={!!m.requireApproval} onChange={(v) => set('members.requireApproval', v)} />
                </SetField>
            </SetBlock>
        </>
    );
};

const SESSIONS = [
    ['MacBook Pro · Local', 'Chrome · current session', true],
    ['iPhone · Local', 'Rush app · 2 hours ago', false],
];

const SecuritySection = ({ s, set }) => (
    <>
        <div className="set-section-head">
            <h2>Security</h2>
            <p>Protect the workspace with single sign-on, two-factor enforcement, and session controls.</p>
        </div>

        <SetBlock title="Authentication">
            <SetField label="Single sign-on (SAML)" desc="Let members sign in through your identity provider.">
                <SetToggle checked={!!s.security.sso} onChange={(v) => set('security.sso', v)} />
            </SetField>
            {s.security.sso && (
                <SetField label="Identity provider" desc="Metadata URL from Okta, Entra ID, or Google Workspace." stack>
                    <SetText
                        mono
                        value={s.security.idpUrl}
                        onChange={(v) => set('security.idpUrl', v)}
                        placeholder="https://idp.example.com/app/saml/metadata"
                    />
                </SetField>
            )}
            <SetField label="Require two-factor authentication" desc="Every member must enable 2FA to access the workspace.">
                <SetToggle checked={!!s.security.enforce2fa} onChange={(v) => set('security.enforce2fa', v)} />
            </SetField>
            <SetField label="Session timeout" desc="Sign members out automatically after inactivity.">
                <SetSelect
                    value={s.security.sessionTimeout}
                    onChange={(v) => set('security.sessionTimeout', v)}
                    options={[
                        { value: '8h', label: '8 hours' },
                        { value: '24h', label: '24 hours' },
                        { value: '7d', label: '7 days' },
                        { value: '30d', label: '30 days' },
                    ]}
                />
            </SetField>
        </SetBlock>

        <SetBlock title="Active sessions" flush>
            {SESSIONS.map(([device, meta, current]) => (
                <div className="set-list-row" key={device}>
                    <div className="set-avatar">{ICN.device}</div>
                    <div className="li-main">
                        <b>{device}</b>
                        <span>{meta}</span>
                    </div>
                    <div className="grow" />
                    {current ? (
                        <span className="set-pill green"><span className="pdot" />This device</span>
                    ) : (
                        <button type="button" className="set-btn sm danger">Revoke</button>
                    )}
                </div>
            ))}
            <div className="set-list-row">
                <div className="grow" />
                <button type="button" className="set-btn sm danger">Sign out all other sessions</button>
            </div>
        </SetBlock>

        <SetBlock title="Audit log" desc="Every admin action is recorded and retained for 365 days.">
            <SetField label="Export audit log" desc="Download a CSV of recent admin and security events.">
                <button type="button" className="set-btn sm">{ICN.download} Export CSV</button>
            </SetField>
        </SetBlock>
    </>
);

const ApiSection = ({ s, set }) => {
    const api = s.api;
    return (
        <>
            <div className="set-section-head">
                <h2>API &amp; webhooks</h2>
                <p>Programmatic access to your workspace. Keep keys secret — anyone with a key can act on the workspace.</p>
            </div>

            <SetBlock title="API keys" flush>
                {(api.keys || []).length === 0 && (
                    <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 13 }}>
                        No keys yet.
                    </div>
                )}
                {(api.keys || []).map((k) => (
                    <div className="set-list-row" key={k.id}>
                        <div className="set-avatar">{ICN.key}</div>
                        <div className="li-main">
                            <b>{k.name}</b>
                            <span className="mono">{k.masked}</span>
                        </div>
                        <div className="grow" />
                        <span className="set-meta">{k.scope}</span>
                        <span className="set-meta dim">Created {k.created}</span>
                        <button type="button" className="set-btn sm">{ICN.copy}</button>
                        <button
                            type="button"
                            className="set-btn sm danger"
                            onClick={() => set('api.keys', api.keys.filter((x) => x.id !== k.id))}
                        >
                            Revoke
                        </button>
                    </div>
                ))}
                <div className="set-list-row">
                    <div className="grow" />
                    <button
                        type="button"
                        className="set-btn sm"
                        onClick={() => {
                            const id = `k${Date.now()}`;
                            set('api.keys', [
                                ...(api.keys || []),
                                { id, name: 'New key', masked: 'rs_••••••••', scope: 'read', created: new Date().toLocaleDateString() },
                            ]);
                        }}
                    >
                        {ICN.plus} Create new key
                    </button>
                </div>
            </SetBlock>

            <SetBlock title="Webhooks" flush desc="Send real-time events to your own endpoints.">
                {(api.hooks || []).length === 0 && (
                    <div style={{ padding: 16, color: 'var(--ink-4)', fontSize: 13 }}>
                        No webhooks yet.
                    </div>
                )}
                {(api.hooks || []).map((h) => (
                    <div className="set-list-row" key={h.id}>
                        <div className="set-avatar">{ICN.hook}</div>
                        <div className="li-main">
                            <b className="mono">{h.url}</b>
                            <span>{h.events}</span>
                        </div>
                        <div className="grow" />
                        <span className={`set-pill ${h.active ? 'green' : 'quiet'}`}>
                            {h.active && <span className="pdot" />}{h.active ? 'active' : 'paused'}
                        </span>
                        <SetToggle
                            checked={h.active}
                            onChange={(v) => set('api.hooks', api.hooks.map((x) => (x.id === h.id ? { ...x, active: v } : x)))}
                        />
                    </div>
                ))}
                <div className="set-list-row">
                    <div className="grow" />
                    <button
                        type="button"
                        className="set-btn sm"
                        onClick={() => {
                            set('api.hooks', [
                                ...(api.hooks || []),
                                { id: `h${Date.now()}`, url: 'https://example.com/hook', events: 'all events', active: true },
                            ]);
                        }}
                    >
                        {ICN.plus} Add endpoint
                    </button>
                </div>
            </SetBlock>
        </>
    );
};

const DangerSection = () => (
    <>
        <div className="set-section-head">
            <h2>Danger zone</h2>
            <p>Irreversible actions that affect the entire workspace. Only owners can complete these.</p>
        </div>

        <SetBlock title="Transfer ownership" danger>
            <SetField label="Transfer this workspace" desc="Hand off billing and full control to another owner. You'll keep your admin role.">
                <button type="button" className="set-btn sm danger">Transfer</button>
            </SetField>
        </SetBlock>

        <SetBlock title="Delete workspace" danger>
            <SetField label="Delete workspace" desc="Permanently removes all projects, translation memory, and glossaries. This cannot be undone.">
                <button type="button" className="set-btn sm danger">{ICN.trash} Delete workspace</button>
            </SetField>
        </SetBlock>
    </>
);

// ============ Main ============
const Admin = () => {
    const token = useSelector((s) => s.auth.token);
    const user = useSelector((s) => s.auth.user) || {};
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [active, setActive] = useState('workspace');
    const [state, setState] = useState(null);
    const [team, setTeam] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        Promise.all([
            axios.get('/api/users/me', { headers }).then((r) => r.data),
            axios.get('/api/team', { headers }).then((r) => r.data).catch(() => []),
        ])
            .then(([me, teamRes]) => {
                setTeam(Array.isArray(teamRes) ? teamRes : []);
                setState({
                    general: {
                        name: me.organization || me.company || '',
                        slug: '',
                        desc: '',
                    },
                    members: {
                        defaultRole: 'translator',
                        autoJoinDomain: false,
                        requireApproval: true,
                    },
                    security: {
                        sso: false,
                        idpUrl: '',
                        enforce2fa: !!me.twoFactorEnabled,
                        sessionTimeout: '24h',
                    },
                    api: { keys: [], hooks: [] },
                });
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load admin settings.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    const set = (path, value) => {
        setState((prev) => deepSet(prev, path, value));
        setDirty(true);
    };

    const save = async () => {
        if (!state) return;
        setSaving(true);
        try {
            await axios.put('/api/users/me', { organization: state.general.name }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDirty(false);
        } catch (e) {
            setError('Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const roleCounts = useMemo(() => {
        const norm = (r) => (r || '').toLowerCase();
        return {
            owners: team.filter((m) => norm(m.role).includes('owner')).length || 1,
            admins: team.filter((m) => norm(m.role).includes('admin')).length,
            translators: team.filter((m) => norm(m.role).includes('translator')).length,
            reviewers: team.filter((m) => norm(m.role).includes('reviewer')).length,
        };
    }, [team]);

    if (!hasAdminPrivileges(user)) {
        return (
            <div className="rs-card" style={{ padding: 48, textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Admin access required</div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 6 }}>
                    Your account doesn't have admin privileges for this workspace.
                </div>
            </div>
        );
    }

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading admin…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;
    if (!state) return null;

    const groups = ['Administration', 'Developer'].map((g) => [g, SECTIONS.filter((s) => s.group === g)]);

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Admin console</h1>
            </div>
            <p className="rs-page-sub">Platform-level controls. Workspace identity, members, security, and developer APIs.</p>

            <div className="set-layout">
                <aside className="set-rail">
                    {groups.map(([gname, items]) => (
                        <div className="set-rail-group" key={gname}>
                            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '4px 12px 6px' }}>
                                {gname}
                            </div>
                            {items.map((sec) => (
                                <button
                                    key={sec.id}
                                    type="button"
                                    className={`set-rail-item ${active === sec.id ? 'on' : ''}`}
                                    onClick={() => setActive(sec.id)}
                                >
                                    <span className="ic">{sec.icon}</span>
                                    <span>{sec.label}</span>
                                </button>
                            ))}
                        </div>
                    ))}
                </aside>

                <div className="set-body">
                    {active === 'workspace' && <WorkspaceSection s={state} set={set} />}
                    {active === 'members' && <MembersSection s={state} set={set} roleCounts={roleCounts} navigate={navigate} />}
                    {active === 'security' && <SecuritySection s={state} set={set} />}
                    {active === 'api' && <ApiSection s={state} set={set} />}
                    {active === 'danger' && <DangerSection />}
                </div>
            </div>

            {dirty && (
                <div style={{
                    position: 'sticky', bottom: 16, marginTop: 16,
                    background: 'var(--bg-card)', border: '1px solid var(--line)',
                    borderRadius: 12, padding: 12, display: 'flex',
                    alignItems: 'center', gap: 12, boxShadow: 'var(--shadow-md)',
                }}>
                    <span style={{ flex: 1, fontSize: 13, color: 'var(--ink-3)' }}>You have unsaved changes.</span>
                    <button type="button" className="btn-q" onClick={() => window.location.reload()}>Discard</button>
                    <button type="button" className="btn-acc" onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            )}
        </>
    );
};

export default Admin;
